import React, { lazy, Component } from 'react';
import { ChevronRight, Sparkles, Leaf, Flame, Tag } from 'lucide-react';
export type Bonus = {
  number: string;
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  realValue: string;
  hook: React.ReactNode;
  bullets: string[];
  testimonial: {
    quote: string;
    name: string;
    location: string;
    Icon: ComponentType<{
      className?: string;
      strokeWidth?: number;
    }>;
  };
};
export const BONUSES: Bonus[] = [{
  number: '01',
  title: 'Protocolo SOS Antojos',
  subtitle: 'Cómo apagar la ansiedad por dulce, los antojos nocturnos y el hambre emocional sin usar fuerza de voluntad',
  image: 'https://cdn.magicpatterns.com/uploads/dLdnND6p7giAJGVK2DMHjg/Bono_1.png',
  imageAlt: 'Taza de té caliente con un cuadrito de chocolate sobre una mesa de madera',
  realValue: '$17',
  hook: <>
        <p className="mb-4" data-id="element-204">
          ¿Sabes ese momento en que son las 10 de la noche, cenaste bien, no
          tienes hambre real, pero algo dentro de ti exige chocolate o pan?
        </p>
        <p className="mb-4" data-id="element-205">
          No es debilidad. Es tu cortisol ganándole la batalla a tu voluntad.
        </p>
        <p data-id="element-206">
          El Protocolo SOS Antojos te da un sistema de intervención de 5 minutos
          para apagar ese ciclo en tiempo real.
        </p>
      </>,
  bullets: ['El Algoritmo SOS de 5 Pasos para interrumpir un antojo antes de que gane', 'Los 7 disparadores hormonales que activan tus antojos sin que lo notes', '10 Snacks Hormonales Inteligentes que satisfacen sin disparar tu insulina', 'Tu Kit de Emergencia Antiantojos para casa, oficina y bolso', 'La Rutina Nocturna Anticraving para cerrar el día sin episodios de hambre emocional', 'Un Mini Plan de 3 Días para estabilizar tus cravings desde esta semana'],
  testimonial: {
    quote: 'Después de entender por qué tenía antojos, dejé de pelear contra ellos. Empecé a responderles diferente. En una semana los antojos nocturnos desaparecieron casi por completo.',
    name: 'Valentina R.',
    location: 'Colombia',
    Icon: Sparkles
  }
}, {
  number: '02',
  title: 'Recetario Hormonal de 15 Minutos',
  subtitle: 'Comidas rápidas que estabilizan tu energía, reducen inflamación y equilibran tus hormonas en tiempo récord',
  image: 'https://cdn.magicpatterns.com/uploads/ay7dGFBXr4zUJytSYWyuQQ/Bono_2.png',
  imageAlt: 'Plato saludable con aguacate, espinacas, salmón ahumado, huevo poché y nueces',
  realValue: '$17',
  hook: <>
        <p className="mb-4" data-id="element-207">
          La mayor objeción que escuchamos de mujeres como tú es esta:
        </p>
        <p className="mb-4 italic text-sand-dark" data-id="element-208">
          "No tengo tiempo para cocinar sano."
        </p>
        <p className="mb-4" data-id="element-209">Este recetario existe precisamente para eso.</p>
        <p data-id="element-210">
          Cada receta fue diseñada con una sola regla: Proteína + Grasa
          Saludable + Fibra Verde. En menos de 15 minutos reales.
        </p>
      </>,
  bullets: ['5 Desayunos Rápidos que anclan tu glucosa desde la mañana y eliminan el antojo de media mañana', '5 Almuerzos Anticrash que eliminan el bajón de energía de las 3 PM', '5 Cenas Reparadoras que activan la quema de grasa nocturna y mejoran tu sueño', '5 Snacks Funcionales para proteger tus tardes sin remordimiento', '5 Bebidas e Infusiones Hormonales para reducir cortisol e inflamación durante el día', 'El Meal Prep del 1%: 20 minutos el domingo para salvar toda tu semana', 'Lista completa de ingredientes base para tener siempre en casa'],
  testimonial: {
    quote: 'Nunca pensé que comer hormonalmente inteligente podía ser tan simple. Las recetas son riquísimas y las preparo mientras hablo por teléfono.',
    name: 'Carolina M.',
    location: 'México',
    Icon: Leaf
  }
}, {
  number: '03',
  title: 'Checklist de Supervivencia Hormonal',
  subtitle: 'Tu hoja de ruta diaria para recuperar energía, reducir inflamación y equilibrar tus hormonas sin pensar demasiado',
  image: 'https://cdn.magicpatterns.com/uploads/mvifc5kRZitubq5wr4qfxg/Bono_3.png',
  imageAlt: 'Tablet con una hoja de ruta diaria sobre un escritorio minimalista con planta y vaso de agua con limón',
  realValue: '$17',
  hook: <>
        <p className="mb-4" data-id="element-211">Saber qué hacer no es suficiente.</p>
        <p className="mb-4" data-id="element-212">
          El verdadero problema es recordarlo y hacerlo cuando estás cansada,
          ocupada y con el cerebro en modo automático.
        </p>
        <p className="mb-4" data-id="element-213">
          La Checklist de Supervivencia Hormonal convierte todo el método en una
          hoja de ruta visual que puedes seguir sin pensar.
        </p>
        <p data-id="element-214">
          Una victoria pequeña a la vez. Un check a la vez. Un 1% mejor cada
          día.
        </p>
      </>,
  bullets: ['Checklist Matutino para activar tu metabolismo y frenar el cortisol desde que abres los ojos', 'Checklist de Tarde para proteger tu energía en las horas más vulnerables del día', 'Checklist Nocturno para preparar tu cuerpo para la reparación hormonal durante el sueño', 'Rastreador de Síntomas Semanal para que veas con tus propios ojos cómo mejoras semana a semana', 'Señales de Mejora que debes observar más allá del peso y la báscula', 'Versión Resumen de Nevera: una sola hoja que puedes imprimir y pegar en tu refrigerador', 'Tu Contrato de Compromiso del 1% para hacer oficial tu decisión de cambiar'],
  testimonial: {
    quote: 'Esta checklist es lo que me faltaba. Tengo la guía completa pero sin esto no sabía por dónde empezar cada día. Ahora es automático.',
    name: 'Sofía L.',
    location: 'España',
    Icon: Flame
  }
}];
type BonusDetailCardProps = {
  bonus: Bonus;
  reversed?: boolean;
  compact?: boolean;
};
/**
 * Reusable detailed card for one bonus. Used by both the dedicated Bonuses
 * section AND the collapsible "Leer más" panel inside the Bundle section.
 */
export function BonusDetailCard({
  bonus,
  reversed = false,
  compact = false
}: BonusDetailCardProps) {
  return <article className={`grid grid-cols-1 ${compact ? 'lg:grid-cols-12' : 'lg:grid-cols-12'} gap-8 md:gap-12 items-center`} data-id="element-215">
      {/* Image */}
      <div className={`lg:col-span-5 ${reversed ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-216">
        <div className="relative" data-id="element-217">
          <div className="absolute -inset-3 bg-sage/30 rounded-2xl -rotate-2" data-id="element-218"></div>
          <div className="relative aspect-square overflow-hidden rounded-2xl shadow-soft-lg" data-id="element-219">
            <img src={bonus.image} alt={bonus.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-220" />
          </div>
          <span className="absolute top-4 left-4 bg-bordo px-4 py-2 rounded-full font-serif text-sm uppercase tracking-[0.15em] text-cream shadow-soft" data-id="element-221">
            Bono · {bonus.number}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className={`lg:col-span-7 ${reversed ? 'lg:order-1' : 'lg:order-2'}`} data-id="element-222">
        <div className="inline-flex items-center gap-2 uppercase tracking-[0.2em] text-[10px] text-sand-dark bg-sand/15 px-3 py-1.5 mb-5" data-id="element-223">
          <Tag strokeWidth={1.5} className="w-3 h-3" data-id="element-224" />
          <span translate="no" data-id="element-225">
            Valor real{' '}
            <span className="line-through opacity-60" data-id="element-226">{bonus.realValue}</span> ·
            Hoy GRATIS
          </span>
        </div>

        <h3 className="font-serif text-2xl md:text-4xl text-ink mb-4 leading-tight" data-id="element-227">
          {bonus.title}
        </h3>

        <p className="text-base md:text-lg text-ink-light font-light leading-relaxed mb-8" data-id="element-228">
          {bonus.subtitle}
        </p>

        <div className="text-base text-ink-light font-light leading-relaxed mb-8 max-w-prose" data-id="element-229">
          {bonus.hook}
        </div>

        <div className="mb-10" data-id="element-230">
          <p className="uppercase tracking-[0.2em] text-xs text-ink-light mb-5" data-id="element-231">
            Dentro de este bono encontrarás
          </p>
          <ul className="space-y-3" data-id="element-232">
            {bonus.bullets.map((b, i) => <li key={i} className="flex items-start gap-3" data-id="element-233">
                <ChevronRight strokeWidth={1.5} className="w-4 h-4 mt-1 flex-shrink-0 text-sand-dark" data-id="element-234" />
                <span className="text-base text-ink-light font-light leading-relaxed" data-id="element-235">
                  {b}
                </span>
              </li>)}
          </ul>
        </div>

        <div className="bg-sage/30 border-l-2 border-sand p-6 md:p-8" data-id="element-236">
          <bonus.testimonial.Icon strokeWidth={1.5} className="w-5 h-5 text-sand-dark mb-4" data-id="element-237" />
          <p className="font-serif text-lg md:text-xl italic text-ink leading-relaxed mb-4" data-id="element-238">
            "{bonus.testimonial.quote}"
          </p>
          <p className="text-xs uppercase tracking-[0.15em] text-ink-light" data-id="element-239">
            <span className="text-ink font-medium" data-id="element-240">
              {bonus.testimonial.name}
            </span>{' '}
            · {bonus.testimonial.location}
          </p>
        </div>
      </div>
    </article>;
}